/**
 * The IT custodian's dashboard (ADR-0025). Rendered by DashboardPage for the
 * `it` role instead of the HR dashboard — asset-custody counts only, no HR data.
 * Three widgets: the action queue (what IT must issue), the catalog by status,
 * and availability by category ("can I fulfil the next request?").
 */
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getAssetDashboard } from '../api/assets.js';
import { Alert } from '../components/Alert.js';

export function AssetDashboard(): JSX.Element {
  const query = useQuery({ queryKey: ['asset', 'dashboard'], queryFn: getAssetDashboard });
  const d = query.data;

  return (
    <section className="p-8 space-y-8 max-w-5xl">
      <header>
        <h1 className="font-serif text-3xl text-white">Dashboard</h1>
        <p className="text-sm text-gray-400">Your asset register at a glance.</p>
      </header>

      {query.isLoading ? <div className="rounded border border-gray-900 px-4 py-6 text-sm text-gray-400">Loading…</div> : null}
      {query.isError ? <Alert variant="error">Could not load the dashboard.</Alert> : null}

      {d ? (
        <>
          {/* Widget 1 — the action queue */}
          <div>
            <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-3">Needs a custodian</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Link to="/assets/requisitions" className="rounded border border-gray-800 bg-gray-950 p-5 hover:border-gray-600">
                <div className="text-4xl font-serif text-white">{d.queue.awaitingIssue}</div>
                <div className="mt-1 text-sm text-gray-300">Awaiting issue</div>
                <div className="text-xs text-gray-500">Approved requests waiting for you to bind a serial →</div>
              </Link>
              <div className="rounded border border-gray-900 p-5 opacity-80">
                <div className="text-4xl font-serif text-gray-300">{d.queue.pendingApproval}</div>
                <div className="mt-1 text-sm text-gray-400">Pending approval</div>
                <div className="text-xs text-gray-600">Waiting on managers — not yours to action yet</div>
              </div>
            </div>
          </div>

          {/* Widget 2 — catalog by status */}
          <div>
            <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-3">Catalog · {d.catalog.total} assets</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard label="Available" value={d.catalog.available} tone="green" />
              <StatCard label="Assigned" value={d.catalog.assigned} tone="blue" />
              <StatCard label="Retired" value={d.catalog.retired} tone="gray" />
              <StatCard label="Lost" value={d.catalog.lost} tone="red" />
            </div>
          </div>

          {/* Widget 3 — availability by category */}
          <div>
            <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-3">Can I fulfil? — availability by category</h2>
            {d.byCategory.length === 0 ? (
              <p className="text-sm text-gray-500">No categories yet. <Link to="/assets/categories" className="underline">Set them up →</Link></p>
            ) : (
              <div className="overflow-x-auto rounded border border-gray-900">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr><th className="px-4 py-2">Category</th><th className="px-4 py-2 text-right">Available</th><th className="px-4 py-2 text-right">Assigned</th></tr>
                  </thead>
                  <tbody>
                    {d.byCategory.map((c) => (
                      <tr key={c.key} className="border-t border-gray-900">
                        <td className="px-4 py-2 text-gray-200">{c.label}</td>
                        <td className={`px-4 py-2 text-right ${c.available === 0 ? 'text-red-400' : 'text-green-300'}`}>{c.available}{c.available === 0 ? ' · none in stock' : ''}</td>
                        <td className="px-4 py-2 text-right text-gray-400">{c.assigned}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'green' | 'blue' | 'gray' | 'red' }): JSX.Element {
  const cls = { green: 'text-green-300', blue: 'text-blue-300', gray: 'text-gray-400', red: 'text-red-400' }[tone];
  return (
    <div className="rounded border border-gray-900 p-4">
      <div className={`text-3xl font-serif ${cls}`}>{value}</div>
      <div className="mt-1 text-sm text-gray-400">{label}</div>
    </div>
  );
}
